import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { CheckCircle2, XCircle, Clock, Play, Upload, Sparkles, Radio, RefreshCw, Tag, Search, X } from "lucide-react";
import AuthMediaPreview from "../components/AuthMediaPreview";
import TrackManagement from "../components/TrackManagement";
import MetaAutofillBanner from "../components/MetaAutofillBanner";
import { readAudioMetadata } from "../lib/mp3meta";
import BatchUploadReview from "../components/BatchUploadReview";
import { extractZipBatch } from "../lib/zipBatch";
import AdminLastLoginBadge from "../components/AdminLastLoginBadge";
import TagChipsInput from "../components/TagChipsInput";
import GenreReviewTool from "../components/GenreReviewTool";
import CommercialBreaksManager from "../components/CommercialBreaksManager";
import ScheduleManager from "../components/ScheduleManager";
import ShofarManager from "../components/ShofarManager";
import { MAIN_GENRES, STYLE_TAG_SUGGESTIONS, MOOD_TAG_SUGGESTIONS } from "../lib/taxonomy";

const CATEGORIES = ["music", "worship", "instrumental", "sermon", "teaching", "study"];
// Assignable station IDs (must match station ids in backend/routes_stations.py).
// Dynamic stations (new-submissions, most-liked, recently-approved) are computed,
// so admins do not tag tracks for them.
const STATIONS = ["truth-hiphop", "worship", "study-instrumentals", "sabbath-flow"];
const MOODS = ["uplifting", "reflective", "energetic", "peaceful", "soulful", "powerful"];

function AdminUploadForm({ onDone }) {
  const [form, setForm] = useState({
    title: "", artist: "", category: "music", genre: "", main_genre: "", description: "",
    lyrics: "", scripture_theme: "", in_rotation: true, featured: false,
  });
  const [styleTags, setStyleTags] = useState([]);
  const [moodTags, setMoodTags] = useState([]);
  const [popularStyle, setPopularStyle] = useState([]);
  const [popularMood, setPopularMood] = useState([]);

  useEffect(() => {
    api.get("/taxonomy/popular-tags?kind=style").then((r) => setPopularStyle(r.data?.tags || [])).catch(() => {});
    api.get("/taxonomy/popular-tags?kind=mood").then((r) => setPopularMood(r.data?.tags || [])).catch(() => {});
  }, []);
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [coverVideo, setCoverVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [audioMeta, setAudioMeta] = useState(null);
  const [autofilled, setAutofilled] = useState(new Set());
  const [coverApplied, setCoverApplied] = useState(false);
  // ZIP batch state
  const [batchRows, setBatchRows] = useState(null);
  const [batchErrors, setBatchErrors] = useState([]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const isZipFile = (f) => !!f && (f.name?.toLowerCase().endsWith(".zip") || f.type === "application/zip" || f.type === "application/x-zip-compressed");

  const onAudioChange = async (file) => {
    setAudio(file);
    setAudioMeta(null);
    setAutofilled(new Set());
    setCoverApplied(false);
    setBatchRows(null);
    setBatchErrors([]);
    setErr("");
    if (!file) return;
    if (isZipFile(file)) {
      const res = await extractZipBatch(file);
      if (res.summary && res.rows.length > 0) {
        setBatchRows(res.rows);
        setBatchErrors(res.errors);
      } else {
        setErr(res.errors?.[0] || "Could not read ZIP file.");
        setAudio(null);
      }
      return;
    }
    const meta = await readAudioMetadata(file);
    setAudioMeta(meta);
  };

  const onAutofillFromMeta = (subset) => {
    if (!subset || Object.keys(subset).length === 0) return;
    setForm((f) => ({ ...f, ...subset }));
    setAutofilled(new Set(Object.keys(subset)));
  };

  const onUseEmbeddedCover = (file) => {
    setCover(file);
    setCoverApplied(true);
  };

  // Per-row uploader for ZIP batch — posts to /admin/tracks (admin direct upload, auto-approved)
  const uploadOneTrack = async (row) => {
    const fd = new FormData();
    fd.append("title", row.title.trim());
    fd.append("artist", row.artist.trim());
    fd.append("category", form.category || "music");
    if (row.genre) fd.append("genre", row.genre);
    if (row.main_genre) fd.append("main_genre", row.main_genre);
    if (row.album) fd.append("description", `From album: ${row.album}`);
    if (row.lyrics) fd.append("lyrics", row.lyrics);
    fd.append("in_rotation", String(!!row.in_rotation));
    fd.append("featured", String(!!row.featured));
    fd.append("audio", row.audioFile);
    if (row.coverFile) fd.append("cover", row.coverFile);
    try {
      const tr = await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = tr?.data?.id || tr?.data?.track?.id;
      // Apply station/mood/energy via PATCH if any provided
      if (newId && (row.station || row.mood || row.energy)) {
        const patch = {};
        if (row.station) patch.station = row.station;
        if (row.mood) patch.mood = row.mood;
        if (row.energy) patch.energy = parseInt(row.energy, 10);
        try { await api.patch(`/admin/tracks/${newId}`, patch); } catch (_e) { /* ignore patch failure */ }
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!audio) { setErr("Please choose an audio file."); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    if (styleTags.length) fd.append("style_tags", styleTags.join(","));
    if (moodTags.length) fd.append("mood_tags", moodTags.join(","));
    fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    if (coverVideo) fd.append("cover_video", coverVideo);
    setSubmitting(true);
    try {
      await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Track uploaded and published.");
      setForm({ title: "", artist: "", category: "music", genre: "", main_genre: "", description: "", lyrics: "", scripture_theme: "", in_rotation: true, featured: false });
      setStyleTags([]); setMoodTags([]);
      setAudio(null); setCover(null); setCoverVideo(null);
      setAudioMeta(null); setAutofilled(new Set()); setCoverApplied(false);
      onDone?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-surface p-6 space-y-4" data-testid="admin-upload-form">
      {!batchRows && (
      <>
      <div className="grid md:grid-cols-2 gap-4">
        <input required={!batchRows} className="input-lux" placeholder="Title *" value={form.title} onChange={onChange("title")} data-testid="admin-upload-title" />
        <input required={!batchRows} className="input-lux" placeholder="Artist *" value={form.artist} onChange={onChange("artist")} data-testid="admin-upload-artist" />
        <select className="input-lux" value={form.category} onChange={onChange("category")} data-testid="admin-upload-category">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input-lux" value={form.main_genre} onChange={onChange("main_genre")} data-testid="admin-upload-main-genre">
          <option value="">Main genre — auto-map from text</option>
          {MAIN_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input className="input-lux md:col-span-2" placeholder="Genre (free text — preserved for backward compat)" value={form.genre} onChange={onChange("genre")} data-testid="admin-upload-genre" />
      </div>
      <TagChipsInput
        label={<><Tag className="inline w-3 h-3 mr-1" strokeWidth={1.5} /> Style / subgenre tags</>}
        value={styleTags}
        onChange={setStyleTags}
        suggestions={STYLE_TAG_SUGGESTIONS}
        popularTags={popularStyle}
        placeholder="e.g. Boom Bap, Lo-fi, Houston Chopped — Enter to add"
        testId="admin-upload-style-tags"
      />
      <TagChipsInput
        label={<><Tag className="inline w-3 h-3 mr-1" strokeWidth={1.5} /> Mood tags</>}
        value={moodTags}
        onChange={setMoodTags}
        suggestions={MOOD_TAG_SUGGESTIONS}
        popularTags={popularMood}
        placeholder="e.g. Reflective, Energetic, Prayerful — Enter to add"
        testId="admin-upload-mood-tags"
        maxItems={10}
      />
      <textarea className="input-lux" placeholder="Description" value={form.description} onChange={onChange("description")} />
      </>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        <input type="file" accept="audio/*,.mp3,.mp4,.wav,.m4a,.ogg,.flac,.zip,application/zip" onChange={(e) => onAudioChange(e.target.files?.[0] || null)} className="text-sm text-[#A1A4AB] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#C5A059] file:text-[#060709] file:font-semibold" data-testid="admin-upload-audio" />
        {!batchRows && <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} className="text-sm text-[#A1A4AB] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E]" data-testid="admin-upload-cover" />}
        {!batchRows && <input type="file" accept="video/mp4,video/webm,.mp4,.webm" onChange={(e) => setCoverVideo(e.target.files?.[0] || null)} className="text-sm text-[#A1A4AB] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E]" data-testid="admin-upload-cover-video" title="Optional animated cover (Suno MP4)" />}
      </div>
      {audio && <div className="text-xs text-[#6C707A]">{audio.name} — {(audio.size / 1024 / 1024).toFixed(2)} MB{batchRows && " · ZIP batch mode"}</div>}
      {audio && audioMeta !== null && !batchRows && (
        <MetaAutofillBanner
          meta={audioMeta}
          formValues={form}
          onAutofill={onAutofillFromMeta}
          onUseCover={onUseEmbeddedCover}
          hasUserCover={!!cover && !coverApplied}
          appliedFields={autofilled}
          appliedCover={coverApplied}
        />
      )}
      {batchRows && (
        <BatchUploadReview
          rows={batchRows}
          setRows={setBatchRows}
          errors={batchErrors}
          mode="admin"
          stations={STATIONS}
          moods={MOODS}
          onCancel={() => { setBatchRows(null); setAudio(null); setBatchErrors([]); }}
          uploader={uploadOneTrack}
          onAllDone={() => { onDone?.(); }}
        />
      )}
      {!batchRows && <div className="text-xs text-[#6C707A]">Audio (required) · Cover image (optional) · Animated cover MP4 (optional — Suno-style moving artwork) · ZIP file = batch upload many MP3s at once</div>}
      {!batchRows && (
      <div className="flex gap-6 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
          <input type="checkbox" checked={form.in_rotation} onChange={onCheck("in_rotation")} className="accent-[#C5A059]" data-testid="admin-upload-rotation" /> Add to live rotation
        </label>
        <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
          <input type="checkbox" checked={form.featured} onChange={onCheck("featured")} className="accent-[#C5A059]" data-testid="admin-upload-featured" /> Feature on home
        </label>
      </div>
      )}
      {err && <div className="text-red-400 text-sm">{err}</div>}
      {msg && <div className="text-emerald-400 text-sm">{msg}</div>}
      {!batchRows && (
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="admin-upload-submit"><Upload className="w-4 h-4" strokeWidth={1.5} /> {submitting ? "Uploading..." : "Publish directly"}</button>
      )}
    </form>
  );
}

function SubmissionCard({ item, onChange }) {
  const [notes, setNotes] = useState(item.admin_notes || "");
  const [category, setCategory] = useState(item.category || "music");
  const [inRotation, setInRotation] = useState(!!item.in_rotation);
  const [featured, setFeatured] = useState(!!item.featured);
  const [station, setStation] = useState(item.station || "");
  const [genre, setGenre] = useState(item.genre || "");
  const [mood, setMood] = useState(item.mood || "");
  const [energy, setEnergy] = useState(item.energy || 3);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Inline cover replacement — admins can swap an inappropriate cover at any
  // status (pending OR approved) without leaving the submissions list.
  const coverInputRef = useRef(null);
  const [coverReplacing, setCoverReplacing] = useState(false);
  const [coverMsg, setCoverMsg] = useState("");

  const replaceCover = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      setErr("Cover must be JPG, PNG, or WebP."); return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr("Cover too large (max 8 MB)."); return;
    }
    setErr(""); setCoverMsg(""); setCoverReplacing(true);
    try {
      const fd = new FormData();
      fd.append("cover", file);
      await api.post(`/admin/tracks/${item.id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCoverMsg("Cover replaced.");
      onChange?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setCoverReplacing(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const approve = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/submissions/${item.id}/approve`, {
        category,
        admin_notes: notes || null,
        in_rotation: inRotation,
        featured,
        station: station || null,
        genre: genre || null,
        mood: mood || null,
        energy: parseInt(energy, 10) || null,
      });
      onChange?.();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!notes || notes.trim().length < 1) {
      setErr("Please provide a note explaining the rejection.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/submissions/${item.id}/reject`, { admin_notes: notes });
      onChange?.();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const cover = coverUrl(item);

  return (
    <div className="card-surface p-5" data-testid={`admin-sub-${item.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-stretch gap-1.5">
          <div className="w-20 h-20 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0 relative">
            {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#6C707A] text-xs">No cover</div>}
            {coverReplacing && (
              <div className="absolute inset-0 grid place-items-center bg-black/60 text-[10px] text-[#C5A059] uppercase tracking-widest">Saving…</div>
            )}
          </div>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            onChange={(e) => replaceCover(e.target.files?.[0])}
            className="hidden"
            data-testid={`admin-sub-cover-input-${item.id}`}
          />
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={coverReplacing}
            className="text-[10px] overline text-[#C5A059] hover:text-[#FDFBF7] border border-[#C5A059]/30 hover:border-[#C5A059] rounded px-2 py-1 transition-colors disabled:opacity-50"
            data-testid={`admin-sub-replace-cover-${item.id}`}
            title="Replace this cover (JPG / PNG / WebP, max 8 MB)"
          >
            {cover ? "Replace cover" : "Add cover"}
          </button>
          {coverMsg && <div className="text-[10px] text-emerald-400 text-center">{coverMsg}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[#FDFBF7] font-medium truncate">{item.title}</div>
              <div className="text-[#A1A4AB] text-sm truncate">{item.artist} {item.uploader_name ? <span className="text-[#6C707A]">· submitted by {item.uploader_name}</span> : null}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="overline text-[#6C707A]">{item.category}</span>
                {item.genre && <span className="overline text-[#6C707A]">· {item.genre}</span>}
                <span className={`overline px-2 py-0.5 rounded-full ${item.status === "approved" ? "text-emerald-400 bg-emerald-500/10" : item.status === "rejected" ? "text-red-400 bg-red-500/10" : "text-[#C5A059] bg-[#C5A059]/10"}`}>{item.status}</span>
              </div>
            </div>
          </div>
          {item.description && <div className="text-sm text-[#A1A4AB] mt-2">{item.description}</div>}
          {item.scripture_theme && <div className="text-xs text-[#6C707A] mt-1"><span className="overline text-[#C5A059]">Scripture · </span>{item.scripture_theme}</div>}
          {item.lyrics && (
            <details className="mt-2">
              <summary className="text-xs text-[#A1A4AB] cursor-pointer hover:text-[#FDFBF7]">View lyrics</summary>
              <pre className="text-xs text-[#A1A4AB] mt-2 whitespace-pre-wrap">{item.lyrics}</pre>
            </details>
          )}
          {/* Always use authenticated blob preview so pending tracks (which require auth) work */}
          <AuthMediaPreview
            trackId={item.id}
            mediaType={item.media_type}
            testIdPrefix={`admin-sub-preview-${item.id}`}
          />
        </div>
      </div>

      {item.status === "pending" && (
        <>
          <div className="gold-line my-4" />
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs overline text-[#6C707A] mb-1 block">Category</label>
              <select className="input-lux" value={category} onChange={(e) => setCategory(e.target.value)} data-testid={`admin-sub-category-${item.id}`}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <label className="flex items-end gap-2 text-sm text-[#A1A4AB] pb-2">
              <input type="checkbox" checked={inRotation} onChange={(e) => setInRotation(e.target.checked)} className="accent-[#C5A059]" data-testid={`admin-sub-rotation-${item.id}`} />
              Add to live rotation
            </label>
            <label className="flex items-end gap-2 text-sm text-[#A1A4AB] pb-2">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-[#C5A059]" data-testid={`admin-sub-featured-${item.id}`} />
              Feature on home
            </label>
          </div>
          <div className="grid md:grid-cols-4 gap-3 mt-3">
            <div>
              <label className="text-xs overline text-[#6C707A] mb-1 block">Station</label>
              <select className="input-lux" value={station} onChange={(e) => setStation(e.target.value)} data-testid={`admin-sub-station-${item.id}`}>
                <option value="">— none —</option>
                {STATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs overline text-[#6C707A] mb-1 block">Genre</label>
              <input className="input-lux" placeholder="e.g. hip-hop, gospel" value={genre} onChange={(e) => setGenre(e.target.value)} data-testid={`admin-sub-genre-${item.id}`} />
            </div>
            <div>
              <label className="text-xs overline text-[#6C707A] mb-1 block">Mood</label>
              <input className="input-lux" placeholder="e.g. uplifting, reflective" value={mood} onChange={(e) => setMood(e.target.value)} data-testid={`admin-sub-mood-${item.id}`} />
            </div>
            <div>
              <label className="text-xs overline text-[#6C707A] mb-1 block">Energy (1–5)</label>
              <input type="number" min="1" max="5" className="input-lux" value={energy} onChange={(e) => setEnergy(e.target.value)} data-testid={`admin-sub-energy-${item.id}`} />
            </div>
          </div>
          <textarea className="input-lux mt-3" placeholder="Notes (required for rejection, optional for approval)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`admin-sub-notes-${item.id}`} />
          {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={approve} disabled={busy} className="btn-primary" data-testid={`admin-sub-approve-${item.id}`}>
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Approve
            </button>
            <button onClick={reject} disabled={busy} className="btn-secondary" data-testid={`admin-sub-reject-${item.id}`}>
              <XCircle className="w-4 h-4" strokeWidth={1.5} /> Reject
            </button>
          </div>
        </>
      )}

      {item.status === "approved" && (
        <>
          <div className="gold-line my-4" />
          <ApprovedControls item={item} onChange={onChange} />
        </>
      )}
    </div>
  );
}

function ApprovedControls({ item, onChange }) {
  const [inRotation, setInRotation] = useState(!!item.in_rotation);
  const [featured, setFeatured] = useState(!!item.featured);
  const [category, setCategory] = useState(item.category);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/tracks/${item.id}`, { in_rotation: inRotation, featured, category });
      onChange?.();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex-1 min-w-[160px]">
        <label className="text-xs overline text-[#6C707A] mb-1 block">Category</label>
        <select className="input-lux" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
        <input type="checkbox" checked={inRotation} onChange={(e) => setInRotation(e.target.checked)} className="accent-[#C5A059]" /> In rotation
      </label>
      <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
        <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-[#C5A059]" /> Featured
      </label>
      <button onClick={save} disabled={busy} className="btn-secondary" data-testid={`admin-track-save-${item.id}`}>Save</button>
      {err && <div className="text-red-400 text-xs w-full">{err}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("pending"); // pending | approved | rejected | all
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the search input — 350ms feels responsive without spamming the API.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(id);
  }, [query]);

  const envIsPreview = typeof window !== "undefined" && window.location.hostname !== "yahwav.com" && window.location.hostname !== "www.yahwav.com";

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (tab !== "all") params.status = tab;
      if (debouncedQuery) params.q = debouncedQuery;
      const [r, c] = await Promise.allSettled([
        api.get("/admin/submissions", { params }),
        api.get("/admin/submissions/counts"),
      ]);
      if (r.status === "fulfilled") setItems(r.value.data || []);
      else setErr(formatApiError(r.reason));
      if (c.status === "fulfilled") setCounts(c.value.data || { pending: 0, approved: 0, rejected: 0, all: 0 });
    } finally { setLoading(false); }
  }, [tab, debouncedQuery]);

  useEffect(() => { load(); }, [load]);

  const tabs = useMemo(() => [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
  ], []);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline text-[#C5A059] mb-2">Control room</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">Admin dashboard</h1>
        <p className="text-[#A1A4AB] mt-2">Review submissions, manage the live rotation, and publish directly.</p>
      </div>

      <AdminLastLoginBadge />

      <section className="space-y-4">
        <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight flex items-center gap-2"><Upload className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} /> Direct upload</h2>
        <AdminUploadForm onDone={load} />
      </section>

      <TrackManagement />

      <GenreReviewTool />

      <CommercialBreaksManager />

      <ScheduleManager />

      <ShofarManager />

      <section className="space-y-4" data-testid="admin-submissions-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} /> Submissions
            {counts.pending > 0 && (
              <span
                className="overline text-[10px] tracking-[0.16em] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200"
                data-testid="admin-pending-count"
                title={`${counts.pending} submission(s) waiting for review`}
              >
                {counts.pending} pending
              </span>
            )}
          </h2>
          <div className="flex flex-wrap gap-2 items-center">
            {tabs.map((t) => {
              const active = tab === t.key;
              const n = counts[t.key] ?? 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`overline px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    active
                      ? "bg-[#C5A059] text-[#060709] border-[#C5A059]"
                      : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
                  }`}
                >
                  {t.label}
                  <span
                    className={`text-[10px] ${active ? "text-[#060709]/70" : "text-[#6C707A]"} tabular-nums`}
                    data-testid={`admin-tab-count-${t.key}`}
                  >
                    {n}
                  </span>
                </button>
              );
            })}
            <button onClick={load} className="btn-ghost text-sm" data-testid="admin-refresh-btn" title="Refresh">
              <RefreshCw className={`w-4 h-4 inline mr-1 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6C707A]" strokeWidth={1.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, artist, uploader name or email…"
            className="input-lux pl-9 pr-9 w-full text-sm"
            data-testid="admin-submissions-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6C707A] hover:text-[#FDFBF7]"
              data-testid="admin-submissions-search-clear"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
        </div>

        {err && <div className="text-red-400 text-sm" data-testid="admin-submissions-error">{err}</div>}

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-lg shimmer" />)}</div>
        ) : items.length === 0 ? (
          <div className="card-surface p-10 text-center" data-testid="admin-submissions-empty">
            <Clock className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-[#FDFBF7] font-serif text-xl">
              {debouncedQuery
                ? `No submissions match "${debouncedQuery}"`
                : tab === "all"
                  ? "No submissions found for this environment."
                  : `No ${tab} submissions`}
            </div>
            <div className="text-[#A1A4AB] text-xs mt-2">
              {envIsPreview
                ? "You're on the preview / test environment. Production submissions live on yahwav.com."
                : "This is the live production database. Counts above reflect real contributor uploads."}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-submissions-list">
            {items.map((it) => <SubmissionCard key={it.id} item={it} onChange={load} />)}
          </div>
        )}
      </section>
    </div>
  );
}
